import { FC } from 'react';
import { FieldError } from 'react-hook-form';
import { IntlShape, useIntl } from 'react-intl';
import { z, ZodType } from 'zod';
import { QueueAction, Schema, SchemaSection } from '@rossum/api-client/schemas';

type InputFormComponentProps<T> = {
  value: T;
  onChange: (v: T) => void;
  schema: Schema;
  schemaContent: SchemaSection[];
  errors: FieldError | undefined;
  alreadyUsedSchemaIds?: string[];
  event: QueueAction['event'];
};

type OutputFormComponentProps<T> = {
  value?: unknown;
  onChange: (v: T) => void;
  schema: Schema;
  errors: FieldError | undefined;
  alreadyUsedSchemaIds?: string[];
  event: QueueAction['event'];
};

export const createForm =
  <T extends ZodType>(
    Form: FC<InputFormComponentProps<z.TypeOf<T>>>,
    _schema: (intl: IntlShape) => T
  ) =>
  ({
    value,
    onChange,
    schema,
    errors,
    alreadyUsedSchemaIds,
    event,
  }: OutputFormComponentProps<z.TypeOf<T>>) => {
    const intl = useIntl();
    // The schema is not used here, but we resolve it for consistency with createHeader
    // and to ensure the intl context is available if needed in the future.
    const _resolvedSchema = _schema(intl);

    return (
      <Form
        onChange={onChange}
        value={value as z.TypeOf<T>}
        schemaContent={schema.content ?? []}
        schema={schema}
        // This type in RHF is turbo ultra wrong
        // Maybe we can do some TS magic to try to infer a working type from the form schema?
        errors={errors}
        alreadyUsedSchemaIds={alreadyUsedSchemaIds}
        event={event}
      />
    );
  };
